import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, Video, Check, Loader2, X, AlertCircle, CalendarX } from 'lucide-react';
import { joinButtonLabel } from '../components/schedule/locationOptions';
import { calendarErrorNotice } from '../lib/calendarWarnings';
import { PublicCardShell, PublicLogo } from '../components/public/PublicCardShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOOKAHEAD_DAYS = 21;
const BRAND = '#1c1917';

// Guest's local IANA tz (sent to the backend + used for all display).
const GUEST_TZ = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
})();

const ymd = (d) => d.toISOString().slice(0, 10);                 // UTC date window bound
const dayKey = (iso) => new Date(iso).toLocaleDateString('en-CA'); // local-tz YYYY-MM-DD bucket
const dayLabel = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const timeLabel = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

// Batch 8a — branded PUBLIC reschedule/cancel landing page. A lead lands here from
// a {{RescheduleLink}} in their booking emails: {app}/reschedule/{bookingId}.
// Loads the existing booking (which already carries its coach), then offers the
// Batch-6 slot picker (reschedule, in the GUEST's tz) + a cancel action.
// No auth — every call hits /api/public/*.
export default function PublicReschedule() {
    const { bookingId } = useParams();

    // page-level lifecycle: loading | notfound | cancelled (already) | main | rescheduled | done(cancelled)
    const [phase, setPhase] = useState('loading');
    const [booking, setBooking] = useState(null);

    // G12 — org branding (color + logo) derived from the booking fetch. Defaults
    // to the stone accent until the booking loads. PublicLogo owns the logo's load
    // error: it hides itself when the org-logo proxy 404s (no logo configured).
    const [brandColor, setBrandColor] = useState(BRAND);
    const [logoUrl, setLogoUrl] = useState(null);

    // slot picker state (only relevant in 'main')
    const [slotPhase, setSlotPhase] = useState('init'); // init | loading | ready | noslots
    const [slotsByDay, setSlotsByDay] = useState({});
    const [days, setDays] = useState([]);
    const [selectedDay, setSelectedDay] = useState(null);
    const [selected, setSelected] = useState(null);
    const [rescheduling, setRescheduling] = useState(false);
    const [rescheduleErr, setRescheduleErr] = useState('');

    // cancel state
    const [showCancel, setShowCancel] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelling, setCancelling] = useState(false);

    // 1) Load the booking. Drives the page phase.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await axios.get(`${API}/public/bookings/${bookingId}`);
                if (!alive) return;
                const b = res.data || {};
                setBooking(b);
                setBrandColor(b.brand?.brand_color || BRAND);
                setLogoUrl(b.brand?.logo_url || (b.org_id ? `${API}/public/org-logo/${b.org_id}` : null));
                setPhase(b.status === 'cancelled' ? 'cancelled' : 'main');
            } catch (e) {
                if (!alive) return;
                setPhase(e?.response?.status === 404 ? 'notfound' : 'notfound');
            }
        })();
        return () => { alive = false; };
    }, [bookingId]);

    // 2) Load the booking's coach slots (next ~21 days). Coach + org come FROM the booking.
    const loadSlots = useCallback(async (uid, orgId) => {
        setSlotPhase('loading');
        const now = new Date();
        const to = new Date(now.getTime() + LOOKAHEAD_DAYS * 86400000);
        try {
            const res = await axios.get(`${API}/public/coach/${uid}/slots`, {
                params: { org_id: orgId, date_from: ymd(now), date_to: ymd(to) },
            });
            const data = res.data || {};
            if (!data.enabled) { setSlotPhase('noslots'); return; }
            const flat = [];
            Object.values(data.slots_by_day || {}).forEach(arr => (arr || []).forEach(s => flat.push(s)));
            flat.sort((a, b) => String(a.start_utc).localeCompare(String(b.start_utc)));
            const grouped = {};
            flat.forEach(s => { const k = dayKey(s.start_utc); (grouped[k] = grouped[k] || []).push(s); });
            const dk = Object.keys(grouped).sort();
            if (!dk.length) { setSlotPhase('noslots'); return; }
            setSlotsByDay(grouped); setDays(dk);
            setSelectedDay(prev => (prev && grouped[prev]) ? prev : dk[0]);
            setSlotPhase('ready');
        } catch { setSlotPhase('noslots'); }
    }, []);

    // Kick off slot load once the booking (with its coach) is in hand and we're on the main view.
    useEffect(() => {
        if (phase === 'main' && booking?.coach_user_id && booking?.org_id) {
            loadSlots(booking.coach_user_id, booking.org_id);
        }
    }, [phase, booking, loadSlots]);

    const confirmReschedule = async () => {
        if (!selected || rescheduling) return;
        setRescheduling(true); setRescheduleErr('');
        try {
            const res = await axios.post(`${API}/public/bookings/${bookingId}/reschedule`, {
                start_utc: selected.start_utc, timezone: GUEST_TZ,
            });
            setBooking(res.data); setPhase('rescheduled');
        } catch (e) {
            const status = e?.response?.status;
            if (status === 409) {
                setRescheduleErr(e?.response?.data?.detail || 'That slot is no longer available — please pick another.');
                setSelected(null);
                loadSlots(booking.coach_user_id, booking.org_id); // refresh availability
            } else {
                setRescheduleErr('Could not reschedule. Please try again.');
            }
        } finally {
            setRescheduling(false);
        }
    };

    const confirmCancel = async () => {
        if (cancelling) return;
        setCancelling(true);
        try {
            await axios.post(`${API}/public/bookings/${bookingId}/cancel`, {
                reason: cancelReason || undefined,
            });
            setPhase('done');
        } catch {
            setCancelling(false); // let them retry; keep them on the cancel UI
        }
    };

    // The shell and the org logo are module-level (components/public/PublicCardShell,
    // #791 E9). They used to be declared here, which gave React a new component
    // type on every render and remounted the whole page, dropping focus out of the
    // cancel-reason textarea after each keystroke.

    // ----- loading -----
    if (phase === 'loading') {
        return (
            <PublicCardShell>
                <div className="flex items-center justify-center gap-2 text-stone-400 text-sm py-16">
                    <Loader2 className="w-5 h-5 animate-spin" /> Loading your booking…
                </div>
            </PublicCardShell>
        );
    }

    // ----- not found -----
    if (phase === 'notfound') {
        return (
            <PublicCardShell>
                <div className="bg-white border border-stone-200 rounded-2xl p-8 text-center shadow-sm">
                    <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center bg-stone-100">
                        <AlertCircle className="w-6 h-6 text-stone-400" />
                    </div>
                    <p className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>We couldn't find this booking</p>
                    <p className="text-sm text-stone-500 mt-2">This link may have expired or the booking no longer exists. If you think this is a mistake, please reach out to the person who scheduled it.</p>
                </div>
            </PublicCardShell>
        );
    }

    // ----- already cancelled -----
    if (phase === 'cancelled') {
        return (
            <PublicCardShell>
                <div className="bg-white border border-stone-200 rounded-2xl p-8 text-center shadow-sm">
                    <PublicLogo src={logoUrl} />
                    <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center bg-stone-100">
                        <CalendarX className="w-6 h-6 text-stone-400" />
                    </div>
                    <p className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>This booking has been cancelled.</p>
                    <p className="text-sm text-stone-500 mt-2">There's nothing more to do here.</p>
                </div>
            </PublicCardShell>
        );
    }

    // ----- rescheduled success -----
    if (phase === 'rescheduled') {
        const b = booking || {};
        return (
            <PublicCardShell>
                <div className="bg-white border border-stone-200 rounded-2xl p-8 text-center shadow-sm">
                    <PublicLogo src={logoUrl} />
                    <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: brandColor }}>
                        <Check className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>You're rescheduled!</p>
                    <p className="text-sm text-stone-600 mt-1">{dayLabel(b.start_utc)} · {timeLabel(b.start_utc)}</p>
                    {/* #514 audit A09: same as the booking confirmation — do not
                        claim an updated invite went out when the calendar write
                        failed. */}
                    {b.calendar_error ? (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2 text-left"
                           data-testid="reschedule-calendar-error">
                            {calendarErrorNotice(b.calendar_error, b.coach?.display_name, { hasMeetingLink: !!b.meet_link })}
                        </p>
                    ) : (
                        <p className="text-xs text-stone-400 mt-1">An updated calendar invite is on its way to your inbox.</p>
                    )}
                    {b.meet_link && (
                        <a href={b.meet_link} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 mt-5 w-full px-5 py-3 rounded-xl text-white font-medium text-sm"
                            style={{ backgroundColor: brandColor }}>
                            {/* meet_link carries the Teams join URL too (#514); the
                                booking's meeting_provider says which it is. */}
                            <Video className="w-4 h-4" /> {joinButtonLabel(b.meeting_provider)}
                        </a>
                    )}
                </div>
            </PublicCardShell>
        );
    }

    // ----- cancelled (just now) success -----
    if (phase === 'done') {
        return (
            <PublicCardShell>
                <div className="bg-white border border-stone-200 rounded-2xl p-8 text-center shadow-sm">
                    <PublicLogo src={logoUrl} />
                    <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center bg-stone-100">
                        <CalendarX className="w-6 h-6 text-stone-500" />
                    </div>
                    <p className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Your booking has been cancelled.</p>
                    <p className="text-xs text-stone-400 mt-2">Changed your mind? You can rebook anytime using your original booking link.</p>
                </div>
            </PublicCardShell>
        );
    }

    // ----- main (confirmed) -----
    const b = booking || {};
    const coach = b.coach || {};
    const coachName = coach.display_name || [coach.first_name].filter(Boolean).join(' ') || 'Your host';
    const slots = slotsByDay[selectedDay] || [];

    return (
        <PublicCardShell>
            {/* Header card: coach + current booking time */}
            <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm">
                <PublicLogo src={logoUrl} />
                <div className="flex items-center gap-3">
                    {coach.photo_url ? (
                        <img src={coach.photo_url} alt={coachName}
                            className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                    ) : (
                        <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white text-base font-semibold"
                            style={{ backgroundColor: brandColor, fontFamily: 'Manrope, sans-serif' }}>
                            {coachName.slice(0, 1).toUpperCase()}
                        </div>
                    )}
                    <div className="min-w-0">
                        <p className="text-base font-semibold text-stone-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{coachName}</p>
                        {coach.title && <p className="text-xs text-stone-500 truncate">{coach.title}</p>}
                    </div>
                </div>
                <div className="mt-4 rounded-xl bg-stone-50 border border-stone-200 px-4 py-3">
                    <p className="text-[11px] uppercase tracking-wide text-stone-400 font-medium">Your current booking</p>
                    <div className="flex items-center gap-2 mt-1">
                        <Calendar className="w-4 h-4 text-stone-400" />
                        <p className="text-sm font-medium text-stone-800">{dayLabel(b.start_utc)} · {timeLabel(b.start_utc)}</p>
                    </div>
                    <p className="text-[11px] text-stone-400 mt-1">{GUEST_TZ.replace(/_/g, ' ')}</p>
                </div>
            </div>

            {/* Reschedule section */}
            <div className="bg-white border border-stone-200 rounded-2xl p-5 mt-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4" style={{ color: brandColor }} />
                    <p className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Pick a new time</p>
                    <span className="text-[11px] text-stone-400 ml-auto">{GUEST_TZ.replace(/_/g, ' ')}</span>
                </div>

                {rescheduleErr && <p className="text-xs text-amber-600 mb-2">{rescheduleErr}</p>}

                {(slotPhase === 'init' || slotPhase === 'loading') && (
                    <div className="flex items-center justify-center gap-2 text-stone-400 text-sm py-8">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading available times…
                    </div>
                )}

                {slotPhase === 'noslots' && (
                    <div className="bg-stone-50 border border-stone-200 rounded-xl p-6 text-center">
                        <Calendar className="w-7 h-7 text-stone-300 mx-auto mb-2" />
                        <p className="text-sm text-stone-500">No times available right now — please cancel below and we'll reach out to reschedule.</p>
                    </div>
                )}

                {slotPhase === 'ready' && (
                    <>
                        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                            {days.map(d => {
                                const active = d === selectedDay;
                                const iso = (slotsByDay[d][0] || {}).start_utc;
                                return (
                                    <button key={d} type="button" onClick={() => { setSelectedDay(d); setSelected(null); }}
                                        className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium border transition ${active ? 'text-white border-transparent' : 'text-stone-600 border-stone-200 hover:border-stone-300'}`}
                                        style={active ? { backgroundColor: brandColor } : {}}>
                                        {dayLabel(iso)}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="grid grid-cols-3 gap-2 mt-3">
                            {slots.map(s => {
                                const active = selected && selected.start_utc === s.start_utc;
                                return (
                                    <button key={s.start_utc} type="button" onClick={() => setSelected(s)}
                                        className={`px-2 py-2 rounded-lg text-xs font-medium border transition ${active ? 'text-white border-transparent' : 'text-stone-700 border-stone-200 hover:border-stone-400'}`}
                                        style={active ? { backgroundColor: brandColor } : {}}>
                                        {timeLabel(s.start_utc)}
                                    </button>
                                );
                            })}
                        </div>

                        {selected && (
                            <button type="button" onClick={confirmReschedule} disabled={rescheduling}
                                className="mt-4 w-full px-5 py-3 rounded-xl text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                                style={{ backgroundColor: brandColor }}>
                                {rescheduling
                                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Rescheduling…</>
                                    : <><Clock className="w-4 h-4" /> Move to {dayLabel(selected.start_utc)} · {timeLabel(selected.start_utc)}</>}
                            </button>
                        )}
                    </>
                )}
            </div>

            {/* Cancel section */}
            <div className="bg-white border border-stone-200 rounded-2xl p-5 mt-4 shadow-sm">
                <div className="flex items-center gap-2">
                    <CalendarX className="w-4 h-4 text-stone-400" />
                    <p className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Cancel this booking</p>
                </div>

                {!showCancel ? (
                    <button type="button" onClick={() => setShowCancel(true)}
                        className="mt-3 w-full px-5 py-2.5 rounded-xl text-stone-700 font-medium text-sm border border-stone-300 hover:bg-stone-50 transition">
                        Cancel booking
                    </button>
                ) : (
                    <div className="mt-3">
                        <textarea
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder="Reason (optional)"
                            rows={3}
                            className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-stone-400 resize-none" />
                        <div className="flex gap-2 mt-3">
                            <button type="button" onClick={() => { setShowCancel(false); setCancelReason(''); }} disabled={cancelling}
                                className="flex-1 px-4 py-2.5 rounded-xl text-stone-600 font-medium text-sm border border-stone-200 hover:bg-stone-50 transition disabled:opacity-60 flex items-center justify-center gap-1.5">
                                <X className="w-4 h-4" /> Keep it
                            </button>
                            <button type="button" onClick={confirmCancel} disabled={cancelling}
                                className="flex-1 px-4 py-2.5 rounded-xl text-white font-medium text-sm bg-red-600 hover:bg-red-700 transition disabled:opacity-60 flex items-center justify-center gap-1.5">
                                {cancelling
                                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Cancelling…</>
                                    : <>Cancel booking</>}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </PublicCardShell>
    );
}
